/**
 * Somaria Tiles — Hamilton Path Puzzle
 * Inspired by The Legend of Zelda: Oracle of Ages tile puzzles.
 *
 * The player moves a character one tile at a time across a grid. Each walkable
 * tile the character steps on becomes activated. The goal is to activate every
 * activatable tile. Walls block movement. Passive tiles (light grey) can be
 * walked on but never become activated. The Cane of Somaria (optional) creates
 * a temporary obstacle that reduces the number of tiles to visit. Somaria blocks
 * can be pushed by walking into them.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

/** Direction the character faces / steps. */
export type Direction = 'up' | 'down' | 'left' | 'right';

/** Cell types in the puzzle grid. */
export enum Cell {
  Empty = 0,
  Wall = 1,
  /** Walkable but cannot be activated. Renders as light grey. */
  Passive = 2,
}

/** A complete puzzle definition. */
export interface PuzzleData {
  title: string;
  description?: string;
  width: number;
  height: number;
  /** Flat array of Cell values, row-major (y * width + x). */
  cells: number[];
  startX: number;
  startY: number;
  /** Whether the Cane of Somaria is available for this puzzle. */
  somaria: boolean;
}

/** Mutable game state during play. */
export interface GameState {
  /** Character position — can be on any walkable tile. */
  posX: number;
  posY: number;
  /** Yellow tile position — only advances to unactivated activatable tiles. */
  yellowX: number;
  yellowY: number;
  facing: Direction;
  /** Flat boolean array matching the puzzle grid. */
  activated: boolean[];
  /** Index of the Somaria block, or null. */
  somariaBlock: number | null;
  /** Number of steps performed. */
  moves: number;
  /** Whether all activatable tiles are activated. */
  won: boolean;
}

/** A puzzle event from Nostr (kind 39443). */
export const PUZZLE_KIND = 39443;

/** Tag used to find puzzles on relays. */
export const PUZZLE_TAG = 'somaria-tiles';

// ─── Direction helpers ──────────────────────────────────────────────────────

const DIRECTIONS: Record<Direction, [number, number]> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

export function directionDelta(dir: Direction): [number, number] {
  return DIRECTIONS[dir];
}

/** Arrow character for a direction (used in UI). */
export function directionArrow(dir: Direction): string {
  switch (dir) {
    case 'up': return '\u2191';
    case 'down': return '\u2193';
    case 'left': return '\u2190';
    case 'right': return '\u2192';
  }
}

export function cellIndex(puzzle: PuzzleData, x: number, y: number): number {
  return y * puzzle.width + x;
}

export function inBounds(puzzle: PuzzleData, x: number, y: number): boolean {
  return x >= 0 && x < puzzle.width && y >= 0 && y < puzzle.height;
}

/**
 * Check whether a tile is empty and walkable (no wall, no Somaria block).
 * Note: this does NOT account for pushable blocks — a tile with a block is
 * considered non-walkable here even though the character can push the block
 * by stepping into it. Use the `step` function for full movement logic.
 */
export function isWalkable(puzzle: PuzzleData, state: GameState, x: number, y: number): boolean {
  if (!inBounds(puzzle, x, y)) return false;
  const idx = cellIndex(puzzle, x, y);
  if (puzzle.cells[idx] === Cell.Wall) return false;
  if (state.somariaBlock === idx) return false;
  return true;
}

// ─── Walkable / activatable helpers ─────────────────────────────────────────

/** Whether a cell type can be activated by stepping on it. */
export function isActivatable(cell: number): boolean {
  return cell === Cell.Empty;
}

/** Whether a cell type can be walked on (not a wall). */
export function isWalkableCell(cell: number): boolean {
  return cell !== Cell.Wall;
}

/** Count total tiles that must be activated (excluding walls, passive tiles, and the Somaria block). */
export function totalWalkable(puzzle: PuzzleData, somariaBlock: number | null): number {
  let count = 0;
  for (let i = 0; i < puzzle.cells.length; i++) {
    if (!isActivatable(puzzle.cells[i])) continue;
    if (somariaBlock === i) continue;
    count++;
  }
  return count;
}

/** Count activated tiles that need activation (excluding passive tiles and the Somaria block). */
export function activatedCount(state: GameState, puzzle: PuzzleData, somariaBlock: number | null): number {
  let count = 0;
  for (let i = 0; i < puzzle.cells.length; i++) {
    if (!isActivatable(puzzle.cells[i])) continue;
    if (somariaBlock === i) continue;
    if (state.activated[i]) count++;
  }
  return count;
}

// ─── Game logic ─────────────────────────────────────────────────────────────

/** Create the initial game state for a puzzle. */
export function createInitialState(puzzle: PuzzleData): GameState {
  const activated = new Array(puzzle.width * puzzle.height).fill(false);
  const startIdx = cellIndex(puzzle, puzzle.startX, puzzle.startY);
  // Only activate the start tile if it's an activatable cell.
  if (isActivatable(puzzle.cells[startIdx])) {
    activated[startIdx] = true;
  }

  return {
    posX: puzzle.startX,
    posY: puzzle.startY,
    yellowX: puzzle.startX,
    yellowY: puzzle.startY,
    facing: 'down',
    activated,
    somariaBlock: null,
    moves: 0,
    won: false,
  };
}

/**
 * Move the character one tile in a direction. Returns a new state (immutable).
 *
 * The character can step onto any walkable tile (empty, activated, or passive).
 * However, a tile is only activated (and the yellow marker advanced) when the
 * character is standing ON the yellow tile and steps onto an unactivated
 * activatable (blue) tile. If the character is not on the yellow tile, it can
 * still move freely but no new tiles are activated.
 *
 * - If the target tile is a wall or out of bounds, the character doesn't move
 *   (but still turns to face that direction).
 * - If the target tile holds a Somaria block, the block is pushed one tile
 *   further — but only if the tile beyond is in bounds and not a wall.
 *   Pushing only activates the uncovered tile when the character is on the
 *   yellow tile.
 */
export function step(state: GameState, puzzle: PuzzleData, dir: Direction): GameState {
  if (state.won) return state;

  const [dx, dy] = directionDelta(dir);
  const nx = state.posX + dx;
  const ny = state.posY + dy;

  // Always update facing direction, even if we can't move.
  const baseState: GameState = { ...state, facing: dir };

  // Out of bounds — can't move, but facing is updated.
  if (!inBounds(puzzle, nx, ny)) {
    return baseState;
  }

  const targetIdx = cellIndex(puzzle, nx, ny);

  // Wall — can't move.
  if (puzzle.cells[targetIdx] === Cell.Wall) {
    return baseState;
  }

  // The character can only activate a new tile if it is currently standing
  // on the yellow tile.
  const onYellow = state.posX === state.yellowX && state.posY === state.yellowY;

  const activated = [...state.activated];
  let somariaBlock = state.somariaBlock;
  let yellowX = state.yellowX;
  let yellowY = state.yellowY;
  let moved = false;

  if (state.somariaBlock === targetIdx) {
    // Target tile has a Somaria block — try to push it.
    const pushX = nx + dx;
    const pushY = ny + dy;

    if (!inBounds(puzzle, pushX, pushY)) {
      return baseState;
    }

    const pushIdx = cellIndex(puzzle, pushX, pushY);

    if (puzzle.cells[pushIdx] === Cell.Wall) {
      return baseState;
    }

    // Push the block and step into its old position.
    somariaBlock = pushIdx;
    moved = true;

    // Only activate the uncovered tile if the character was on the yellow
    // tile and the target is an unactivated activatable tile.
    if (onYellow && isActivatable(puzzle.cells[targetIdx]) && !activated[targetIdx]) {
      activated[targetIdx] = true;
      yellowX = nx;
      yellowY = ny;
    }
  } else {
    // Normal step onto an empty, activated, or passive tile.
    moved = true;

    // Only activate if the character was on the yellow tile and the target
    // is an unactivated activatable tile.
    if (onYellow && isActivatable(puzzle.cells[targetIdx]) && !activated[targetIdx]) {
      activated[targetIdx] = true;
      yellowX = nx;
      yellowY = ny;
    }
  }

  const newState: GameState = {
    ...baseState,
    posX: nx,
    posY: ny,
    yellowX,
    yellowY,
    activated,
    somariaBlock,
    moves: moved ? state.moves + 1 : state.moves,
  };

  newState.won = checkWin(newState, puzzle);
  return newState;
}

/**
 * Use the Cane of Somaria — create or remove the obstacle in front of the
 * character. The previous block (if any) is always removed first. If there is
 * already a block in front of the character, it is simply removed (toggle
 * behaviour). Otherwise a new block is placed on the tile the character faces,
 * provided that tile is in bounds, not a wall, and not the character's own tile.
 */
export function useCane(state: GameState, puzzle: PuzzleData): GameState {
  if (state.won || !puzzle.somaria) return state;

  const [dx, dy] = directionDelta(state.facing);
  const nx = state.posX + dx;
  const ny = state.posY + dy;

  // Default: remove any existing block.
  let somariaBlock: number | null = null;

  if (inBounds(puzzle, nx, ny)) {
    const idx = cellIndex(puzzle, nx, ny);
    // Can't place on a wall or on the character's current tile.
    if (puzzle.cells[idx] !== Cell.Wall && idx !== cellIndex(puzzle, state.posX, state.posY)) {
      // If there's already a block here, just remove it (toggle off).
      // Otherwise place a new block.
      if (state.somariaBlock !== idx) {
        somariaBlock = idx;
      }
    }
  }

  const newState: GameState = {
    ...state,
    somariaBlock,
  };

  // Placing / removing a block can change the win condition.
  newState.won = checkWin(newState, puzzle);
  return newState;
}

/** Check whether all activatable tiles (excluding walls, passive tiles, and the Somaria block) are activated. */
export function checkWin(state: GameState, puzzle: PuzzleData): boolean {
  for (let i = 0; i < puzzle.cells.length; i++) {
    if (!isActivatable(puzzle.cells[i])) continue;
    if (state.somariaBlock === i) continue;
    if (!state.activated[i]) return false;
  }
  return true;
}

// ─── Encoding / decoding ────────────────────────────────────────────────────

/** Encode a PuzzleData into a compact URL-safe string (base64 of JSON). */
export function encodePuzzle(puzzle: PuzzleData): string {
  const json = JSON.stringify(puzzle);
  // Use encodeURIComponent + btoa for Unicode safety.
  return btoa(encodeURIComponent(json));
}

/** Decode a URL-safe string back into a PuzzleData. Throws on invalid input. */
export function decodePuzzle(encoded: string): PuzzleData {
  const json = decodeURIComponent(atob(encoded));
  const data = JSON.parse(json) as PuzzleData;
  return validatePuzzle(data);
}

/** Validate a parsed puzzle object. Throws if invalid. */
export function validatePuzzle(data: unknown): PuzzleData {
  if (typeof data !== 'object' || data === null) {
    throw new Error('Invalid puzzle: not an object');
  }
  const d = data as Record<string, unknown>;

  const width = d.width;
  const height = d.height;
  if (typeof width !== 'number' || typeof height !== 'number' ||
      width < 1 || height < 1 || width > 30 || height > 30) {
    throw new Error('Invalid puzzle dimensions');
  }

  const cells = d.cells;
  if (!Array.isArray(cells) || cells.length !== width * height) {
    throw new Error('Invalid puzzle cells');
  }
  for (const c of cells) {
    if (c !== Cell.Empty && c !== Cell.Wall && c !== Cell.Passive) {
      throw new Error('Invalid cell value');
    }
  }

  const startX = d.startX;
  const startY = d.startY;
  if (typeof startX !== 'number' || typeof startY !== 'number' ||
      !inBounds({ width, height, cells } as PuzzleData, startX, startY)) {
    throw new Error('Invalid start position');
  }
  if (cells[startY * width + startX] === Cell.Wall) {
    throw new Error('Start position cannot be on a wall');
  }

  const title = typeof d.title === 'string' ? d.title : 'Untitled Puzzle';
  const description = typeof d.description === 'string' ? d.description : undefined;
  const somaria = d.somaria === true;

  return { title, description, width, height, cells, startX, startY, somaria };
}

/** Create a blank puzzle with given dimensions. */
export function createBlankPuzzle(width: number, height: number): PuzzleData {
  const cells = new Array(width * height).fill(Cell.Empty);
  const startX = Math.floor(width / 2);
  const startY = Math.floor(height / 2);
  return {
    title: 'Untitled Puzzle',
    description: '',
    width,
    height,
    cells,
    startX,
    startY,
    somaria: false,
  };
}

/** Deep-clone a puzzle. */
export function clonePuzzle(p: PuzzleData): PuzzleData {
  return { ...p, cells: [...p.cells] };
}

// ─── Sample puzzles ─────────────────────────────────────────────────────────

export const SAMPLE_PUZZLES: PuzzleData[] = [
  {
    title: 'First Steps',
    description: 'A simple 4x3 puzzle to learn the basics. Step across every blue tile to turn them all red!',
    width: 4,
    height: 3,
    cells: [
      0, 0, 0, 0,
      0, 0, 0, 0,
      0, 0, 0, 0,
    ],
    startX: 0,
    startY: 0,
    somaria: false,
  },
  {
    title: 'The T-Junction',
    description: 'A T-shaped room. Plan your route to cover every tile without getting stuck!',
    width: 5,
    height: 3,
    cells: [
      0, 0, 0, 0, 0,
      0, 1, 1, 1, 0,
      0, 0, 0, 0, 0,
    ],
    startX: 0,
    startY: 0,
    somaria: false,
  },
  {
    title: 'Zigzag Corridor',
    description: 'A winding corridor. Follow the path and cover every tile!',
    width: 6,
    height: 5,
    cells: [
      0, 0, 0, 0, 0, 0,
      1, 1, 1, 1, 1, 0,
      0, 0, 0, 0, 0, 0,
      0, 1, 1, 1, 1, 1,
      0, 0, 0, 0, 0, 0,
    ],
    startX: 0,
    startY: 0,
    somaria: false,
  },
  {
    title: 'Cane Trial',
    description: 'Use the Cane of Somaria to block off tiles you cannot reach. Press SPACE to place an obstacle in the direction you face. Walk into a block to push it!',
    width: 5,
    height: 5,
    cells: [
      0, 0, 0, 0, 0,
      0, 0, 0, 0, 0,
      0, 0, 0, 0, 0,
      0, 0, 0, 0, 0,
      0, 0, 0, 0, 0,
    ],
    startX: 0,
    startY: 0,
    somaria: true,
  },
  {
    title: 'Somaria Maze',
    description: 'A 5x5 grid with a blocked center. Use the Cane of Somaria to create obstacles and find your path through every tile.',
    width: 5,
    height: 5,
    cells: [
      0, 0, 0, 0, 0,
      0, 0, 0, 0, 0,
      0, 0, 1, 0, 0,
      0, 0, 0, 0, 0,
      0, 0, 0, 0, 0,
    ],
    startX: 0,
    startY: 0,
    somaria: true,
  },
  {
    title: 'Grey Corridor',
    description: 'Light grey tiles can be walked on but never turn red. Use them as passages to reach every blue tile!',
    width: 6,
    height: 5,
    cells: [
      0, 0, 2, 2, 0, 0,
      0, 0, 2, 2, 0, 0,
      0, 0, 2, 2, 0, 0,
      0, 0, 2, 2, 0, 0,
      0, 0, 0, 0, 0, 0,
    ],
    startX: 0,
    startY: 0,
    somaria: false,
  },
];
